import { Assessment as AssessmentIcon, Flight, Home, SportsSoccer, Square } from '@mui/icons-material';
import {
  Box,
  Chip,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
} from '@mui/material';
import PropTypes from 'prop-types';
import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import theme from '../../styles/theme';
import { capitalizar, formatClubTeamName, formatFecha } from '../../utils/UtilesComun';
import { obtenerColorNota } from '../../utils/UtilesJugador';
import {
  AlertApp,
  BadgeResultadoPartidoApp,
  BoxTitleApp,
  PaperApp,
  SelectWrapperApp,
  TablePaginationApp,
  TypographyApp,
} from '../custom/CustomComponents';

/**
 * Componente modular que muestra la tabla de historial de partidos, estadísticas
 * y valoraciones de un jugador en una temporada.
 * Puede ser utilizado tanto de forma embebida en la ficha del jugador como dentro de un modal.
 */
const TablaHistorialRendimiento = ({
  partidos = [],
  temporada,
  onTemporadaChange,
  temporadas,
  historicoJugador = [],
  categoriaFallback = null,
  notaMediaFallback = null,
  loading = false,
  error = null,
  embedded = false,
  hasPagination = embedded,
  title = 'Rendimiento',
  maxHeight = 520,
  onPartidoClick,
}) => {
  const navigate = useNavigate();

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Lista de temporadas disponibles para el selector
  const temporadasDisponibles = useMemo(() => {
    if (temporadas && temporadas.length > 0) {
      return temporadas;
    }
    const setTemps = new Set();
    if (temporada) {
      setTemps.add(temporada);
    }
    if (historicoJugador && Array.isArray(historicoJugador)) {
      historicoJugador.forEach((h) => {
        if (h.temporada) {
          setTemps.add(h.temporada);
        }
      });
    }
    return Array.from(setTemps).sort().reverse();
  }, [temporadas, temporada, historicoJugador]);

  React.useEffect(() => {
    setPage(0);
  }, [partidos, temporada]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleTemporadaChange = (nuevaTemp) => {
    if (onTemporadaChange) {
      onTemporadaChange(nuevaTemp);
    }
  };

  // Paginación de partidos
  const displayedPartidos = hasPagination
    ? partidos.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
    : partidos;

  // Cálculos de métricas de resumen (sobre el total de la temporada)
  const partidosConNota = partidos.filter((p) => p.calificacion !== null && p.calificacion !== undefined);
  const notaMedia =
    partidosConNota.length > 0
      ? Number(
          (partidosConNota.reduce((acc, p) => acc + Number(p.calificacion), 0) / partidosConNota.length).toFixed(1)
        )
      : (notaMediaFallback ?? null);

  const estiloNotaMedia = obtenerColorNota(notaMedia);
  const totalTitularidades = partidos.filter((p) => p.titular === true).length;
  const totalMinutos = partidos.reduce((acc, p) => acc + (p.minutos || 0), 0);
  const totalGoles = partidos.reduce((acc, p) => acc + (p.goles || 0), 0);
  const totalAmarillas = partidos.reduce((acc, p) => acc + (p.amarillas || 0), 0);
  const totalRojas = partidos.reduce((acc, p) => acc + (p.rojas || 0), 0);

  const isLoadingEmpty = loading && partidos.length === 0;

  const handleNavigatePartido = (idPartido) => {
    if (onPartidoClick) {
      onPartidoClick(idPartido);
    } else {
      navigate(`/partido/${idPartido}`);
    }
  };

  const contenidoTabla = (
    <Box sx={{ width: '100%' }}>
      {error && (
        <AlertApp severity="error" sx={{ mb: 1.5 }}>
          {error}
        </AlertApp>
      )}

      {/* Fila de métricas de resumen sobre el fondo unificado de la cabecera */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexWrap: 'wrap',
          gap: 1.25,
          px: 1.5,
          py: 1.25,
          m: 0,
          backgroundColor: theme.palette.custom.tablas,
          borderBottom: `1px solid ${theme.palette.divider}`,
          borderTop: embedded ? `1px solid rgba(0, 0, 0, 0.06)` : 'none',
        }}
      >
        {/* Nota Media */}
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.75,
            px: 1,
            height: 28,
            borderRadius: '6px',
            backgroundColor: '#ffffff',
            border: `1px solid ${theme.palette.divider}`,
          }}
        >
          <TypographyApp sx={{ fontSize: '0.74rem', fontWeight: 600, color: 'text.secondary' }}>
            Nota Media
          </TypographyApp>
          <Chip
            size="small"
            label={notaMedia !== null && notaMedia !== undefined ? notaMedia.toFixed(1) : '-'}
            sx={{
              backgroundColor: estiloNotaMedia.bg,
              color: estiloNotaMedia.color,
              border: `1px solid ${estiloNotaMedia.border}`,
              fontWeight: 800,
              fontSize: '0.78rem',
              height: 20,
              minWidth: 38,
              borderRadius: '4px',
              '& .MuiChip-label': { px: 0.5 },
            }}
          />
        </Box>

        {/* Partidos */}
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.75,
            px: 1,
            height: 28,
            borderRadius: '6px',
            backgroundColor: '#ffffff',
            border: `1px solid ${theme.palette.divider}`,
          }}
        >
          <TypographyApp sx={{ fontSize: '0.74rem', fontWeight: 500, color: 'text.secondary' }}>Partidos</TypographyApp>
          <TypographyApp sx={{ fontSize: '0.80rem', fontWeight: 700, color: 'text.primary' }}>
            {isLoadingEmpty ? '-' : partidos.length}
          </TypographyApp>
        </Box>

        {/* Titularidades */}
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.75,
            px: 1,
            height: 28,
            borderRadius: '6px',
            backgroundColor: '#ffffff',
            border: `1px solid ${theme.palette.divider}`,
          }}
        >
          <TypographyApp sx={{ fontSize: '0.74rem', fontWeight: 500, color: 'text.secondary' }}>
            Titularidades
          </TypographyApp>
          <TypographyApp sx={{ fontSize: '0.80rem', fontWeight: 700, color: 'text.primary' }}>
            {isLoadingEmpty ? '-' : totalTitularidades}
          </TypographyApp>
        </Box>

        {/* Minutos */}
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.75,
            px: 1,
            height: 28,
            borderRadius: '6px',
            backgroundColor: '#ffffff',
            border: `1px solid ${theme.palette.divider}`,
          }}
        >
          <TypographyApp sx={{ fontSize: '0.74rem', fontWeight: 500, color: 'text.secondary' }}>Minutos</TypographyApp>
          <TypographyApp sx={{ fontSize: '0.80rem', fontWeight: 700, color: 'text.primary' }}>
            {isLoadingEmpty ? '-' : `${totalMinutos}'`}
          </TypographyApp>
        </Box>

        {/* Goles */}
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.6,
            px: 1,
            height: 28,
            borderRadius: '6px',
            backgroundColor: totalGoles > 0 ? '#f0fdf4' : '#ffffff',
            border: `1px solid ${totalGoles > 0 ? '#86efac' : theme.palette.divider}`,
          }}
        >
          <SportsSoccer
            sx={{
              fontSize: '0.85rem',
              color: totalGoles > 0 ? '#15803d' : 'text.secondary',
            }}
          />
          <TypographyApp
            sx={{
              fontSize: '0.74rem',
              fontWeight: 500,
              color: totalGoles > 0 ? '#15803d' : 'text.secondary',
            }}
          >
            Goles
          </TypographyApp>
          <TypographyApp
            sx={{
              fontSize: '0.80rem',
              fontWeight: 700,
              color: totalGoles > 0 ? '#15803d' : 'text.primary',
            }}
          >
            {isLoadingEmpty ? '-' : totalGoles}
          </TypographyApp>
        </Box>

        {/* Amarillas */}
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.6,
            px: 1,
            height: 28,
            borderRadius: '6px',
            backgroundColor: totalAmarillas > 0 ? '#fefce8' : '#ffffff',
            border: `1px solid ${totalAmarillas > 0 ? '#fde047' : theme.palette.divider}`,
          }}
        >
          <Square
            sx={{
              fontSize: '0.80rem',
              color: totalAmarillas > 0 ? '#ca8a04' : '#94a3b8',
            }}
          />
          <TypographyApp
            sx={{
              fontSize: '0.74rem',
              fontWeight: 500,
              color: totalAmarillas > 0 ? '#a16207' : 'text.secondary',
            }}
          >
            Amarillas
          </TypographyApp>
          <TypographyApp
            sx={{
              fontSize: '0.80rem',
              fontWeight: 700,
              color: totalAmarillas > 0 ? '#a16207' : 'text.primary',
            }}
          >
            {isLoadingEmpty ? '-' : totalAmarillas}
          </TypographyApp>
        </Box>

        {/* Rojas */}
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.6,
            px: 1,
            height: 28,
            borderRadius: '6px',
            backgroundColor: totalRojas > 0 ? '#fef2f2' : '#ffffff',
            border: `1px solid ${totalRojas > 0 ? '#fca5a5' : theme.palette.divider}`,
          }}
        >
          <Square
            sx={{
              fontSize: '0.80rem',
              color: totalRojas > 0 ? '#dc2626' : '#94a3b8',
            }}
          />
          <TypographyApp
            sx={{
              fontSize: '0.74rem',
              fontWeight: 500,
              color: totalRojas > 0 ? '#b91c1c' : 'text.secondary',
            }}
          >
            Rojas
          </TypographyApp>
          <TypographyApp
            sx={{
              fontSize: '0.80rem',
              fontWeight: 700,
              color: totalRojas > 0 ? '#b91c1c' : 'text.primary',
            }}
          >
            {isLoadingEmpty ? '-' : totalRojas}
          </TypographyApp>
        </Box>
      </Box>

      {/* Tabla detallada de partidos y valoraciones */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 6 }}>
          <CircularProgress size={30} />
        </Box>
      ) : partidos.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 5 }}>
          <TypographyApp sx={{ color: 'text.secondary', fontSize: '0.88rem' }}>
            No hay registros de partidos ni valoraciones para este jugador en la temporada seleccionada.
          </TypographyApp>
        </Box>
      ) : (
        <>
          <TableContainer
            sx={{
              width: '100%',
              ...(hasPagination ? {} : { maxHeight: maxHeight, overflowY: 'auto' }),
            }}
          >
            <Table size="small" stickyHeader={!hasPagination} sx={{ tableLayout: 'fixed', width: '100%' }}>
              <TableHead>
                <TableRow
                  sx={{
                    '& th': { fontWeight: 700, fontSize: '0.75rem', py: 0.75, px: 0.5 },
                    '& th:first-of-type': { pl: '24px !important' },
                  }}
                >
                  <TableCell sx={{ width: '4%', textAlign: 'center', pl: '24px !important' }}>J</TableCell>
                  <TableCell sx={{ width: '5%' }}>Fecha</TableCell>
                  <TableCell sx={{ width: '14%' }}>Equipo</TableCell>
                  <TableCell sx={{ width: '24%' }}>Rival</TableCell>
                  <TableCell sx={{ width: '6.5%', textAlign: 'center' }}>Resultado</TableCell>
                  <TableCell sx={{ width: '4%', textAlign: 'center' }}>T/S</TableCell>
                  <TableCell sx={{ width: '4.5%', textAlign: 'center' }}>Min</TableCell>
                  <TableCell sx={{ width: '3.5%', textAlign: 'center' }}>Goles</TableCell>
                  <TableCell sx={{ width: '3.5%', textAlign: 'center' }}>Tarj.</TableCell>
                  <TableCell sx={{ width: '5.5%', textAlign: 'center' }}>Nota</TableCell>
                  <TableCell sx={{ width: '25.5%', pl: 1 }}>Observación</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {displayedPartidos.map((partido) => {
                  const { corta: fechaStr, completa: fechaCompleta } = formatFecha(partido.fecha);
                  const estiloNotaPartido = obtenerColorNota(partido.calificacion);
                  const tieneNotaPartido =
                    partido.calificacion !== null && partido.calificacion !== undefined && partido.calificacion !== '';

                  // 1. Detección de localía y equipo propio descartando el rival
                  let esLocalReal = partido.esLocal;
                  let rawNombreEquipo = partido.nombreEquipo;
                  let idEquipoReal = partido.idEquipo;

                  if (!rawNombreEquipo) {
                    const rivalNorm = partido.rival?.trim().toLowerCase();
                    const localNorm = partido.nombreLocal?.trim().toLowerCase();
                    const visNorm = partido.nombreVisitante?.trim().toLowerCase();

                    if (rivalNorm && visNorm && rivalNorm === visNorm) {
                      rawNombreEquipo = partido.nombreLocal;
                      if (esLocalReal === null || esLocalReal === undefined) {
                        esLocalReal = true;
                      }
                      if (!idEquipoReal) {
                        idEquipoReal = partido.idLocal;
                      }
                    } else if (rivalNorm && localNorm && rivalNorm === localNorm) {
                      rawNombreEquipo = partido.nombreVisitante;
                      if (esLocalReal === null || esLocalReal === undefined) {
                        esLocalReal = false;
                      }
                      if (!idEquipoReal) {
                        idEquipoReal = partido.idVisitante;
                      }
                    } else {
                      rawNombreEquipo = esLocalReal ? partido.nombreLocal : partido.nombreVisitante;
                      if (!idEquipoReal) {
                        idEquipoReal = esLocalReal ? partido.idLocal : partido.idVisitante;
                      }
                    }
                  }

                  let idRivalReal = partido.idRival;
                  if (!idRivalReal) {
                    if (esLocalReal === true) {
                      idRivalReal = partido.idVisitante;
                    } else if (esLocalReal === false) {
                      idRivalReal = partido.idLocal;
                    } else if (idEquipoReal) {
                      idRivalReal = idEquipoReal === partido.idLocal ? partido.idVisitante : partido.idLocal;
                    }
                  }

                  // 2. Obtención de la categoría con fallback al histórico del jugador
                  let categoriaReal = partido.categoriaEquipo;

                  if (!categoriaReal && historicoJugador && historicoJugador.length > 0) {
                    if (idEquipoReal) {
                      const matchId = historicoJugador.find((h) => h.idEquipo === idEquipoReal);
                      if (matchId?.categoria) {
                        categoriaReal = matchId.categoria;
                      }
                    }
                    if (!categoriaReal && rawNombreEquipo) {
                      const normNombre = rawNombreEquipo.trim().toLowerCase();
                      const matchNombre = historicoJugador.find(
                        (h) => h.nombreEquipo?.trim().toLowerCase() === normNombre
                      );
                      if (matchNombre?.categoria) {
                        categoriaReal = matchNombre.categoria;
                      }
                    }
                    if (!categoriaReal) {
                      const matchTemp = historicoJugador.find((h) => h.temporada === (partido.temporada || temporada));
                      if (matchTemp?.categoria) {
                        categoriaReal = matchTemp.categoria;
                      }
                    }
                    if (!categoriaReal && historicoJugador[0]?.categoria) {
                      categoriaReal = historicoJugador[0].categoria;
                    }
                  }

                  if (!categoriaReal && categoriaFallback) {
                    categoriaReal = categoriaFallback;
                  }

                  // 3. Formatear como "Categoría Letra" (ej. "Cadetes B", "Cadetes", "Infantiles B")
                  const textoEquipo = formatClubTeamName(rawNombreEquipo, categoriaReal);

                  return (
                    <TableRow
                      key={partido.idPartido}
                      hover
                      sx={{
                        '& td': { py: 0.5, px: 0.5, fontSize: '0.8rem' },
                        '& td:first-of-type': { pl: '24px !important' },
                      }}
                    >
                      {/* Jornada */}
                      <TableCell sx={{ textAlign: 'center', fontWeight: 600, pl: '24px !important', pr: 0.5 }}>
                        J{partido.jornada || '-'}
                      </TableCell>

                      {/* Fecha */}
                      <TableCell sx={{ px: 0.5 }}>
                        <Tooltip title={fechaCompleta} arrow>
                          <span>{fechaStr}</span>
                        </Tooltip>
                      </TableCell>

                      {/* Equipo con el que jugó el partido (Categoría + Letra) */}
                      <TableCell sx={{ px: 0.5, overflow: 'hidden' }}>
                        <Tooltip
                          title={rawNombreEquipo ? `${textoEquipo} (${rawNombreEquipo})` : textoEquipo || '-'}
                          arrow
                        >
                          <Box
                            onClick={idEquipoReal ? () => navigate(`/equipo/${idEquipoReal}`) : undefined}
                            sx={{
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              cursor: idEquipoReal ? 'pointer' : 'default',
                              '&:hover': idEquipoReal ? { textDecoration: 'underline' } : {},
                            }}
                          >
                            <TypographyApp
                              sx={{
                                fontSize: '0.8rem',
                                fontWeight: idEquipoReal ? 600 : 500,
                                color: idEquipoReal ? 'primary.dark' : 'text.primary',
                                '&:hover': idEquipoReal ? { color: 'primary.main' } : {},
                              }}
                              handleOverflow
                            >
                              {textoEquipo || '-'}
                            </TypographyApp>
                          </Box>
                        </Tooltip>
                      </TableCell>

                      {/* Rival y Localía */}
                      <TableCell sx={{ px: 0.5, overflow: 'hidden' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, overflow: 'hidden' }}>
                          {esLocalReal !== null && esLocalReal !== undefined && (
                            <Tooltip title={esLocalReal ? 'Local' : 'Visitante'} arrow>
                              {esLocalReal ? (
                                <Home sx={{ fontSize: '0.95rem', color: 'text.secondary', flexShrink: 0 }} />
                              ) : (
                                <Flight sx={{ fontSize: '0.95rem', color: 'text.secondary', flexShrink: 0 }} />
                              )}
                            </Tooltip>
                          )}
                          <Tooltip title={capitalizar(partido.rival || 'Rival')} arrow>
                            <Box
                              onClick={idRivalReal ? () => navigate(`/equipo/${idRivalReal}`) : undefined}
                              sx={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                cursor: idRivalReal ? 'pointer' : 'default',
                                '&:hover': idRivalReal ? { textDecoration: 'underline' } : {},
                              }}
                            >
                              <TypographyApp
                                sx={{
                                  fontSize: '0.8rem',
                                  fontWeight: idRivalReal ? 600 : 500,
                                  color: idRivalReal ? 'primary.dark' : 'text.primary',
                                  '&:hover': idRivalReal ? { color: 'primary.main' } : {},
                                }}
                                handleOverflow
                              >
                                {capitalizar(partido.rival || 'Rival')}
                              </TypographyApp>
                            </Box>
                          </Tooltip>
                        </Box>
                      </TableCell>

                      {/* Resultado / Enlace a Acta */}
                      <TableCell sx={{ textAlign: 'center', px: 0.25 }}>
                        <BadgeResultadoPartidoApp
                          partido={partido}
                          onClick={() => handleNavigatePartido(partido.idPartido)}
                        />
                      </TableCell>

                      {/* Titularidad (T / S / NC) */}
                      <TableCell sx={{ textAlign: 'center', px: 0.25 }}>
                        {partido.convocado === false ? (
                          <Tooltip title="No convocado" arrow>
                            <Chip
                              size="small"
                              label="NC"
                              sx={{
                                height: 20,
                                minWidth: 26,
                                fontSize: '0.68rem',
                                fontWeight: 700,
                                backgroundColor: '#fee2e2',
                                color: '#991b1b',
                                border: '1px solid #fca5a5',
                                '& .MuiChip-label': { px: 0.4 },
                              }}
                            />
                          </Tooltip>
                        ) : partido.titular ? (
                          <Tooltip title="Titular" arrow>
                            <Chip
                              size="small"
                              label="T"
                              sx={{
                                height: 20,
                                minWidth: 26,
                                fontSize: '0.68rem',
                                fontWeight: 700,
                                backgroundColor: '#dcfce7',
                                color: '#14532d',
                                border: '1px solid #86efac',
                                '& .MuiChip-label': { px: 0.4 },
                              }}
                            />
                          </Tooltip>
                        ) : (
                          <Tooltip title="Suplente" arrow>
                            <Chip
                              size="small"
                              label="S"
                              sx={{
                                height: 20,
                                minWidth: 26,
                                fontSize: '0.68rem',
                                fontWeight: 700,
                                backgroundColor: '#f1f5f9',
                                color: '#475569',
                                border: '1px solid #cbd5e1',
                                '& .MuiChip-label': { px: 0.4 },
                              }}
                            />
                          </Tooltip>
                        )}
                      </TableCell>

                      {/* Minutos */}
                      <TableCell sx={{ textAlign: 'center', fontWeight: 500, px: 0.25 }}>
                        {partido.minutos !== null && partido.minutos !== undefined ? `${partido.minutos}'` : '-'}
                      </TableCell>

                      {/* Goles */}
                      <TableCell sx={{ textAlign: 'center', px: 0.25 }}>
                        {partido.goles > 0 ? (
                          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.3 }}>
                            <SportsSoccer sx={{ fontSize: '0.85rem', color: theme.palette.primary.main }} />
                            <span style={{ fontWeight: 700 }}>{partido.goles}</span>
                          </Box>
                        ) : (
                          '-'
                        )}
                      </TableCell>

                      {/* Tarjetas */}
                      <TableCell sx={{ textAlign: 'center', px: 0.25 }}>
                        {partido.amarillas > 0 ? (
                          <Square sx={{ fontSize: '0.85rem', color: '#eab308' }} />
                        ) : partido.rojas > 0 ? (
                          <Square sx={{ fontSize: '0.85rem', color: '#ef4444' }} />
                        ) : (
                          '-'
                        )}
                      </TableCell>

                      {/* Nota con escala cromática */}
                      <TableCell sx={{ textAlign: 'center', px: 0.5 }}>
                        <Chip
                          size="small"
                          label={tieneNotaPartido ? Number(partido.calificacion).toFixed(1) : '-'}
                          sx={{
                            backgroundColor: estiloNotaPartido.bg,
                            color: estiloNotaPartido.color,
                            border: `1px solid ${estiloNotaPartido.border}`,
                            fontWeight: 700,
                            fontSize: '0.78rem',
                            height: 22,
                            width: 44,
                            minWidth: 44,
                            maxWidth: 44,
                            borderRadius: '4px',
                            '& .MuiChip-label': { px: 0.4 },
                          }}
                        />
                      </TableCell>

                      {/* Observación cualitativa */}
                      <TableCell
                        sx={{
                          py: 0.75,
                          px: 1,
                          verticalAlign: 'middle',
                          wordBreak: 'break-word',
                          overflowWrap: 'break-word',
                          whiteSpace: 'normal',
                        }}
                      >
                        {partido.textoObservacion ? (
                          <TypographyApp
                            sx={{
                              fontSize: '0.76rem',
                              fontStyle: 'italic',
                              color: 'text.secondary',
                              whiteSpace: 'normal',
                              wordBreak: 'break-word',
                              overflowWrap: 'break-word',
                              lineHeight: 1.35,
                            }}
                          >
                            {partido.textoObservacion}
                          </TypographyApp>
                        ) : (
                          <span style={{ color: '#94a3b8' }}>-</span>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          {hasPagination && partidos.length > 0 && (
            <TablePaginationApp
              component="div"
              count={partidos.length}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[10, 20, 50]}
              labelRowsPerPage="Filas:"
              sx={{ borderTop: `1px solid ${theme.palette.divider}` }}
            />
          )}
        </>
      )}
    </Box>
  );

  if (embedded) {
    return (
      <PaperApp sx={{ p: 0, overflow: 'hidden', border: `0.5px solid ${theme.palette.primary.main}` }}>
        <BoxTitleApp
          conFondo
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '0.9rem',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <AssessmentIcon sx={{ fontSize: '1.2rem', color: theme.palette.primary.main }} />
              <span style={{ fontWeight: 600 }}>{title}</span>
            </Box>
            {onTemporadaChange && temporadasDisponibles.length > 0 ? (
              <>
                <Box sx={{ width: '1px', height: 18, backgroundColor: theme.palette.divider, mx: 0.25 }} />
                <SelectWrapperApp
                  value={temporada || temporadasDisponibles[0]}
                  onChange={(e) => handleTemporadaChange(e.target.value)}
                  variant="standard"
                  disableUnderline
                  options={temporadasDisponibles}
                  renderLabel={(temp) => `${temp}`}
                  sx={{
                    width: 'auto',
                    minWidth: 115,
                    height: 30,
                    backgroundColor: '#ffffff',
                    border: '1px solid #cbd5e1',
                    borderRadius: '6px',
                    boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
                    transition: 'all 0.15s ease-in-out',
                    '&:hover': {
                      borderColor: theme.palette.primary.main,
                      backgroundColor: '#ffffff',
                    },
                    '& .MuiSelect-select': {
                      py: 0.25,
                      pl: 1.25,
                      pr: '28px !important',
                      fontSize: '0.82rem',
                      fontWeight: 600,
                      color: 'text.primary',
                      display: 'flex',
                      alignItems: 'center',
                    },
                    '& .MuiSvgIcon-root': {
                      fontSize: '1.15rem',
                      color: 'text.secondary',
                      right: 4,
                    },
                  }}
                />
              </>
            ) : (
              temporada && (
                <Chip
                  label={temporada}
                  size="small"
                  sx={{
                    fontWeight: 600,
                    fontSize: '0.72rem',
                    height: 24,
                    backgroundColor: '#ffffff',
                    color: 'text.primary',
                    border: '1px solid #cbd5e1',
                    ml: 0.5,
                  }}
                />
              )
            )}
          </Box>
        </BoxTitleApp>
        {contenidoTabla}
      </PaperApp>
    );
  }

  return contenidoTabla;
};

TablaHistorialRendimiento.propTypes = {
  partidos: PropTypes.array,
  temporada: PropTypes.string,
  onTemporadaChange: PropTypes.func,
  temporadas: PropTypes.array,
  historicoJugador: PropTypes.array,
  categoriaFallback: PropTypes.string,
  notaMediaFallback: PropTypes.number,
  loading: PropTypes.bool,
  error: PropTypes.string,
  embedded: PropTypes.bool,
  hasPagination: PropTypes.bool,
  title: PropTypes.string,
  maxHeight: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onPartidoClick: PropTypes.func,
};

export default TablaHistorialRendimiento;
